import { Checkbox, Form, Input, Modal, Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { AnnotationStandardDetails } from '../../../AnnotationManager/AnnotationStandardForm/AnnotationStandardForm.slice';
import { PublishRequest } from '../Landing.slice';
import PublishFilters from './PublishFilters';
import { areFiltersInValidState, areFindingIdsInValidState, AVAILABLE_SEQUENCES, FilterRow, KeyValue } from './utils';

const reviewedFindingsOnlyMessage =
  'Findings that have been reviewed will be published in Results & Reporting portal. Other unreviewed findings can be published if added in Finding Type filter. Other unreviewed findings will be removed from Results & Reporting.';
const excludeNotCompletedAssessmentMessage =
  'Findings which are subject of Engineering Assessment and have not been completed will not be published. These findings will also be removed from Results & Reporting if they are published already.';

const { Option } = Select;

type Props = {
  showModal: boolean;
  selectedTurbineIds: string[];
  standard: AnnotationStandardDetails | null;
  data: any[];
  isPublishing: boolean;
  getTurbinesStandard?: (selectedTurbineId: string) => void;
  onHideModal: (show: boolean) => void;
  onSubmit: (request: PublishRequest) => void;
};

const PublishModal = ({ showModal, selectedTurbineIds, standard, data, isPublishing, getTurbinesStandard, onHideModal, onSubmit }: Props) => {
  const [findingTypes, setFindingTypes] = useState<string[]>([]);
  const [findingIds, setFindingIds] = useState<string>('');
  const [sequences, setSequences] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [reviewsCompleted, setReviewsCompleted] = useState<boolean>(true);
  const [assessmentsCompleted, setAssessmentsCompleted] = useState<boolean>(true);

  useEffect(() => {
    if (selectedTurbineIds.length) {
      var turbine = data.find(x => x.key === selectedTurbineIds[0]);
      if (turbine && getTurbinesStandard) {
        getTurbinesStandard(turbine.standardId);
      }
    }
  }, [data, getTurbinesStandard, selectedTurbineIds]);

  const onOk = () => {
    const request: PublishRequest = {
      selectedTurbinePaths: data.filter(x => selectedTurbineIds.includes(x.key)).map(x => x.key),
      findingTypes,
      findingIds: findingIds
        .replace(' ', '')
        .split(',')
        .filter(x => x),
      filters,
      sequences,
      assessmentsCompleted,
      reviewsCompleted
    };

    onSubmit(request);
  };

  const isValid = () => {
    if (isPublishing) {
      return false;
    }

    if (filters.length) {
      const areFiltersValid = areFiltersInValidState(filters);
      if (!areFiltersValid) {
        return false;
      }
    }

    if (findingIds.length) {
      const areFindingIdsValid = areFindingIdsInValidState(findingIds);
      if (!areFindingIdsValid) {
        return false;
      }
    }

    return true;
  };

  if (!standard) {
    return null;
  }

  const availableFindingTypes: KeyValue[] = standard.findingTypes.map(x => ({
    value: x.id!,
    text: x.findingTypeTemplate?.isMLRelated ? `(ML) ${x.findingTypeTemplate!.name!}` : x.findingTypeTemplate!.name!
  }));

  return (
    <Modal
      className='bulk-publish'
      title='Publish'
      cancelText='Close'
      okText='Publish'
      onOk={() => onOk()}
      visible={showModal}
      okButtonProps={{ disabled: !isValid(), loading: isPublishing }}
      onCancel={() => onHideModal(false)}
    >
      <Form layout='vertical'>
        <Form.Item label='Finding Type' colon={false}>
          <Select
            mode='multiple'
            value={findingTypes}
            showSearch
            style={{ minWidth: '150px' }}
            allowClear={true}
            placeholder='Please select'
            optionFilterProp='children'
            onChange={setFindingTypes}
            dropdownMatchSelectWidth={false}
          >
            {availableFindingTypes.map(option => (
              <Option key={option.value} value={option.value}>
                {option.text}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label='Finding ID (Comma (,) separated list)'>
          <Input style={{ minWidth: '150px' }} allowClear value={findingIds} onChange={e => setFindingIds(e.target.value)} />
        </Form.Item>
        <Form.Item label='Sequences' colon={false}>
          <Select
            mode='multiple'
            value={sequences}
            showSearch
            style={{ minWidth: '150px' }}
            allowClear={true}
            placeholder='Please select'
            optionFilterProp='children'
            onChange={setSequences}
            dropdownMatchSelectWidth={false}
          >
            {AVAILABLE_SEQUENCES.map(option => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label='Filters' colon={false}>
          <PublishFilters {...{ filters, standard, setFilters }} />
        </Form.Item>
        <Form.Item label='Options' colon={false}>
          <Checkbox checked={reviewsCompleted} onChange={e => setReviewsCompleted(e.target.checked)}>
            Reviewed findings
          </Checkbox>
          <Tooltip placement='right' title={reviewedFindingsOnlyMessage}>
            <InfoCircleOutlined />
          </Tooltip>
          <br></br>
          <Checkbox checked={assessmentsCompleted} onChange={e => setAssessmentsCompleted(e.target.checked)} style={{ marginTop: '5px' }}>
            Exclude not completed assessment
          </Checkbox>
          <Tooltip placement='right' title={excludeNotCompletedAssessmentMessage}>
            <InfoCircleOutlined />
          </Tooltip>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PublishModal;
